import { Auth0Provider, AppState } from "@auth0/auth0-react";
import React, { PropsWithChildren } from "react";
import { useNavigate } from "react-router-dom";

interface Auth0ProviderWithNavigateProps {
  children: React.ReactNode;
}

export default function Auth0ProviderWithNavigate({
  children,
}: PropsWithChildren<Auth0ProviderWithNavigateProps>): JSX.Element | null {
  const navigate = useNavigate();

  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const redirectUri = process.env.REACT_APP_AUTH0_CALLBACK_URL;
  // The audience must match the API Identifier configured in the Auth0 dashboard and the
  // backend's Auth0:Audience. Without it Auth0 returns an opaque token the API can't
  // validate; with it Auth0 issues a JWT access token scoped to our API.
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  if (!(domain && clientId && redirectUri)) {
    return null;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        ...(audience ? { audience } : {}),
        scope: "openid profile email",
      }}
      // Use refresh tokens + localStorage so access tokens renew reliably without depending
      // on third-party cookies (which browsers like Safari block, breaking silent-iframe
      // renewal in production). Requires "Allow Offline Access" enabled on the API in Auth0.
      useRefreshTokens
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}
